import React from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Textarea } from '../../../components/ui/Textarea';
import type { DocumentTemplateField } from '../../../core/models/Document';

interface DynamicFieldEditorProps {
  field?: DocumentTemplateField;
  onSave: (field: DocumentTemplateField) => void;
  onCancel: () => void;
}

export const DynamicFieldEditor: React.FC<DynamicFieldEditorProps> = ({
  field,
  onSave,
  onCancel,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<DocumentTemplateField>({
    defaultValues: field || {
      key: '',
      label: '',
      type: 'text',
      required: false,
      options: [],
      defaultValue: '',
      placeholder: '',
      helpText: '',
    },
  });

  const fieldType = watch('type');
  const isSelectType = fieldType === 'select';

  const onSubmit = (data: DocumentTemplateField) => {
    onSave(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Input
          label="Field Key"
          {...register('key', {
            required: 'Field key is required',
            pattern: {
              value: /^[a-z][a-z0-9_]*$/i,
              message: 'Key must start with a letter and contain only letters, numbers, and underscores',
            },
          })}
          error={errors.key?.message}
          placeholder="e.g., client_name"
        />

        <Input
          label="Field Label"
          {...register('label', { required: 'Field label is required' })}
          error={errors.label?.message}
          placeholder="e.g., Client Name"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Field Type</label>
        <Select
          value={fieldType}
          onValueChange={(value) => setValue('type', value as DocumentTemplateField['type'])}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select field type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="text">Text</SelectItem>
            <SelectItem value="number">Number</SelectItem>
            <SelectItem value="date">Date</SelectItem>
            <SelectItem value="boolean">Boolean</SelectItem>
            <SelectItem value="select">Select (Dropdown)</SelectItem>
            <SelectItem value="multiline">Multiline Text</SelectItem>
            <SelectItem value="client">Client Data</SelectItem>
            <SelectItem value="services">Services</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          {...register('required')}
          onCheckedChange={(checked) => setValue('required', checked === true)}
        />
        <label className="text-sm font-medium">Required field</label>
      </div>

      {isSelectType && (
        <div className="space-y-2">
          <label className="text-sm font-medium">Options (one per line)</label>
          <Textarea
            {...register('options')}
            placeholder="Option 1&#10;Option 2&#10;Option 3"
            rows={4}
            onChange={(e) => {
              const options = e.target.value.split('\n').filter((o) => o.trim());
              setValue('options', options);
            }}
          />
        </div>
      )}

      <Input
        label="Placeholder"
        {...register('placeholder')}
        placeholder="Enter placeholder text"
      />

      <Textarea
        label="Help Text"
        {...register('helpText')}
        placeholder="Additional information about this field"
        rows={2}
      />

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {field ? 'Update Field' : 'Add Field'}
        </Button>
      </div>
    </form>
  );
};
